import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  IconButton,
  Button,
  Tooltip,
  Popover,
  alpha,
  useTheme,
  useMediaQuery,
  Fab,
  Stack,
  SwipeableDrawer,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  CalendarDays,
  Calendar,
  List,
  Bug,
  Lightbulb,
  Zap,
  HelpCircle,
  FileText,
  Lock,
  Repeat,
  ChevronRight as ChevronRightIcon,
  X,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { openModal } from '@/store/slices/uiSlice';
import { taskService, leaveService, calendarService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { Task, TaskStatus, TaskPriority, LeaveRequest, LeaveStatus, IssueType, CalendarEvent } from '@/types';
import PriorityBadge from '@/shared/components/PriorityBadge';
import { isAdminOrAbove } from '@/shared/utils/roles';

// Issue type config
const issueTypeConfig: Record<IssueType, { icon: React.FC<any>; color: string; label: string }> = {
  bug: { icon: Bug, color: '#dc2626', label: 'Bug' },
  feature: { icon: Lightbulb, color: '#16a34a', label: 'Feature' },
  improvement: { icon: Zap, color: '#4F46E5', label: 'Improvement' },
  task: { icon: FileText, color: '#6b7280', label: 'Task' },
  question: { icon: HelpCircle, color: '#9333ea', label: 'Question' },
};

type ViewMode = 'month' | 'week';

/**
 * Google Calendar–style month cells:
 * - Fixed height so the grid never stretches
 * - Compact single-line chips; long titles truncate with ellipsis
 * - Only as many chips as fit; "+N more" opens a day agenda popover
 */
const MONTH_CHIP_H = 18;
const MONTH_DATE_ROW_H = 28;
const MONTH_CELL_H_MD = 136;
const MONTH_CELL_MAX_ITEMS = Math.max(
  1,
  Math.floor((MONTH_CELL_H_MD - MONTH_DATE_ROW_H - 10) / (MONTH_CHIP_H + 2)) - 1
);
const WEEK_VISIBLE_TASKS = 12;

/**
 * CalendarPage
 * Calendar view for tasks with month/week views
 * Deadline-centric planning interface
 * Mobile-first responsive design
 */
const CalendarPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectCurrentUser);
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [dayPopoverAnchor, setDayPopoverAnchor] = useState<null | HTMLElement>(null);
  const [mobileTaskDrawer, setMobileTaskDrawer] = useState<{ open: boolean; date: Dayjs | null; tasks: Task[] }>({ open: false, date: null, tasks: [] });
  const [showRecurring, setShowRecurring] = useState(true);

  const rangeStart = (viewMode === 'week' ? currentDate.startOf('week') : currentDate.startOf('month')).format('YYYY-MM-DD');
  const rangeEnd = (viewMode === 'week' ? currentDate.endOf('week') : currentDate.endOf('month')).format('YYYY-MM-DD');

  // Load every task due in the visible range (the list API used to return only the first page)
  const { data: tasksData } = useQuery({
    queryKey: [...QUERY_KEYS.tasks, 'calendar', rangeStart, rangeEnd],
    queryFn: async () => {
      const pageSize = 500;
      const filters = { dueDateFrom: rangeStart, dueDateTo: rangeEnd };
      const first = await taskService.getTasks(filters, 1, pageSize);
      if (!first.hasMore) return first;
      const items = [...first.items];
      let page = 2;
      let hasMore = first.hasMore;
      while (hasMore && page <= 20) {
        const next = await taskService.getTasks(filters, page, pageSize);
        items.push(...next.items);
        hasMore = next.hasMore;
        page += 1;
      }
      return { ...first, items, hasMore: false };
    },
  });

  const tasks = tasksData?.items ?? [];

  // Fetch leave requests from API
  const { data: leaveResponse } = useQuery({
    queryKey: [...QUERY_KEYS.calendarEvents, currentDate.format('YYYY-MM')],
    queryFn: () => {
      const startOfMonth = currentDate.startOf('month').toISOString();
      const endOfMonth = currentDate.endOf('month').toISOString();
      return leaveService.getLeaveCalendar(startOfMonth, endOfMonth);
    },
  });
  const canSeeAllLeaves = isAdminOrAbove(user?.role);
  const leaveRequests: LeaveRequest[] = useMemo(() => {
    const items = leaveResponse?.data ?? [];
    if (canSeeAllLeaves) return items;
    return items.filter((leave) => leave.userId === user?.id);
  }, [leaveResponse?.data, canSeeAllLeaves, user?.id]);

  const { data: holidays = [] } = useQuery({
    queryKey: [...QUERY_KEYS.holidays, currentDate.format('YYYY-MM'), viewMode],
    queryFn: () => {
      const start = (viewMode === 'week' ? currentDate.startOf('week') : currentDate.startOf('month')).format('YYYY-MM-DD');
      const end = (viewMode === 'week' ? currentDate.endOf('week') : currentDate.endOf('month')).format('YYYY-MM-DD');
      return calendarService.getHolidays(start, end);
    },
  });

  // Get tasks for a specific date
  const getTasksForDate = (date: Dayjs): Task[] => {
    const day = date.format('YYYY-MM-DD');
    const filteredTasks = tasks.filter((task: Task) => task.dueDate?.slice(0, 10) === day);
    
    // Apply recurring filter if needed
    if (!showRecurring) {
      return filteredTasks.filter((task: Task) => !task.isRecurring);
    }
    
    return filteredTasks;
  };

  // Get leave requests for a specific date
  const getLeavesForDate = (date: Dayjs): LeaveRequest[] => {
    return leaveRequests.filter((leave: LeaveRequest) => {
      const leaveStart = dayjs(leave.startDate);
      const leaveEnd = dayjs(leave.endDate);
      return (
        (date.isAfter(leaveStart, 'day') || date.isSame(leaveStart, 'day')) &&
        (date.isBefore(leaveEnd, 'day') || date.isSame(leaveEnd, 'day'))
      );
    });
  };

  const getHolidaysForDate = (date: Dayjs): CalendarEvent[] => {
    return holidays.filter((holiday) => dayjs(holiday.startDate).isSame(date, 'day'));
  };

  // Navigation
  const handlePrev = () => {
    if (viewMode === 'month') {
      setCurrentDate(currentDate.subtract(1, 'month'));
    } else {
      setCurrentDate(currentDate.subtract(1, 'week'));
    }
  };

  const handleNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(currentDate.add(1, 'month'));
    } else {
      setCurrentDate(currentDate.add(1, 'week'));
    }
  };

  const handleToday = () => {
    setCurrentDate(dayjs());
  };

  /** Open Google Calendar–style day agenda (all items for that date). */
  const openDayAgenda = (date: Dayjs, anchorEl: HTMLElement) => {
    setSelectedDate(date);
    setDayPopoverAnchor(anchorEl);
  };

  const handleDayPopoverClose = () => {
    setDayPopoverAnchor(null);
    setSelectedDate(null);
  };

  const handleCreateTask = () => {
    const dueDate = selectedDate?.format('YYYY-MM-DD');
    dispatch(openModal({
      type: 'taskCreate',
      data: dueDate ? { dueDate } : {},
    }));
    handleDayPopoverClose();
  };

  const handleViewAllTasks = () => {
    handleDayPopoverClose();
    navigate('/tasks');
  };

  const handleTaskClick = (taskId: string) => {
    navigate(`/tasks?taskId=${taskId}`);
  };

  // Generate calendar days for month view (pad trailing cells so the last week is complete)
  const getMonthDays = () => {
    const startOfMonth = currentDate.startOf('month');
    const startDay = startOfMonth.day();
    const daysInMonth = currentDate.daysInMonth();

    const days: (Dayjs | null)[] = [];

    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(startOfMonth.date(i));
    }

    while (days.length % 7 !== 0) {
      days.push(null);
    }

    return days;
  };

  // Generate week days
  const getWeekDays = () => {
    const startOfWeek = currentDate.startOf('week');
    const days: Dayjs[] = [];

    for (let i = 0; i < 7; i++) {
      days.push(startOfWeek.add(i, 'day'));
    }

    return days;
  };

  // Get priority color
  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case TaskPriority.P1:
        return '#ef4444';
      case TaskPriority.P2:
        return '#f59e0b';
      case TaskPriority.P3:
        return '#4F46E5';
      case TaskPriority.P4:
        return '#94a3b8';
      default:
        return '#64748b';
    }
  };

  const weekDayNames = isMobile ? ['S', 'M', 'T', 'W', 'T', 'F', 'S'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Mobile: Handle date tap to show tasks
  const handleMobileDateTap = (date: Dayjs) => {
    const dayTasks = getTasksForDate(date);
    setSelectedDate(date);
    setMobileTaskDrawer({ open: true, date, tasks: dayTasks });
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: { xs: 2, md: 3 },
        }}
      >
        <Box>
          <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={600}>
            Calendar
          </Typography>
          {!isMobile && (
            <Typography variant="body2" color="text.secondary">
              Plan and visualize your task deadlines
            </Typography>
          )}
        </Box>
        {!isMobile && (
          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            onClick={handleCreateTask}
          >
            Add Task
          </Button>
        )}
      </Box>

      {/* Calendar Controls */}
      <Card sx={{ mb: { xs: 2, md: 3 }, borderRadius: { xs: 2, md: 1 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: { xs: 1.5, md: 2 },
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 2 }, flex: isMobile ? '1 1 100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-start' }}>
            <IconButton onClick={handlePrev} size={isMobile ? 'small' : 'medium'}>
              <ChevronLeft size={isMobile ? 18 : 20} />
            </IconButton>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={600} sx={{ textAlign: 'center', minWidth: { xs: 'auto', md: 200 } }}>
              {viewMode === 'month'
                ? currentDate.format(isMobile ? 'MMM YYYY' : 'MMMM YYYY')
                : `${currentDate.startOf('week').format('MMM D')} - ${currentDate.endOf('week').format(isMobile ? 'D' : 'MMM D, YYYY')}`}
            </Typography>
            <IconButton onClick={handleNext} size={isMobile ? 'small' : 'medium'}>
              <ChevronRight size={isMobile ? 18 : 20} />
            </IconButton>
            {!isMobile && (
              <Button variant="outlined" size="small" onClick={handleToday}>
                Today
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5, bgcolor: 'grey.100', borderRadius: 1, p: 0.5 }}>
            <IconButton
              onClick={() => setViewMode('month')}
              size="small"
              sx={{
                p: { xs: 0.75, md: 1 },
                bgcolor: viewMode === 'month' ? 'background.paper' : 'transparent',
                color: viewMode === 'month' ? 'primary.main' : 'text.secondary',
                boxShadow: viewMode === 'month' ? 1 : 0,
              }}
            >
              <CalendarDays size={isMobile ? 16 : 18} />
            </IconButton>
            <IconButton
              onClick={() => setViewMode('week')}
              size="small"
              sx={{
                p: { xs: 0.75, md: 1 },
                bgcolor: viewMode === 'week' ? 'background.paper' : 'transparent',
                color: viewMode === 'week' ? 'primary.main' : 'text.secondary',
                boxShadow: viewMode === 'week' ? 1 : 0,
              }}
            >
              <List size={isMobile ? 16 : 18} />
            </IconButton>
            {isMobile && (
              <Button size="small" onClick={handleToday} sx={{ minWidth: 'auto', px: 1.5, fontSize: '0.75rem' }}>
                Today
              </Button>
            )}
          </Box>

          {/* Recurring Tasks Filter */}
          {!isMobile && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showRecurring}
                  onChange={(e) => setShowRecurring(e.target.checked)}
                  size="small"
                  icon={<Repeat size={16} style={{ opacity: 0.3 }} />}
                  checkedIcon={<Repeat size={16} />}
                />
              }
              label={<Typography variant="caption">Show recurring</Typography>}
              sx={{ ml: 1 }}
            />
          )}
        </Box>

        {/* Week Day Headers */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            bgcolor: 'grey.50',
          }}
        >
          {weekDayNames.map((day) => (
            <Box
              key={day}
              sx={{
                p: { xs: 0.75, md: 1.5 },
                textAlign: 'center',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant={isMobile ? 'caption' : 'subtitle2'} color="text.secondary" fontWeight={500}>
                {day}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Calendar Grid */}
        {viewMode === 'month' ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            }}
          >
            {getMonthDays().map((day, index) => {
              const cellSx = {
                height: { xs: 56, md: MONTH_CELL_H_MD },
                minHeight: { xs: 56, md: MONTH_CELL_H_MD },
                maxHeight: { xs: 56, md: MONTH_CELL_H_MD },
                overflow: 'hidden',
                borderBottom: '1px solid',
                borderRight: (index + 1) % 7 !== 0 ? '1px solid' : 'none',
                borderColor: 'divider',
              } as const;

              if (!day) {
                return (
                  <Box
                    key={`empty-${index}`}
                    sx={{
                      ...cellSx,
                      bgcolor: 'grey.50',
                    }}
                  />
                );
              }

              const dayTasks = getTasksForDate(day);
              const dayLeaves = getLeavesForDate(day);
              const dayHolidays = getHolidaysForDate(day);
              const isToday = day.isSame(dayjs(), 'day');
              const isSelected = selectedDate?.isSame(day, 'day') && Boolean(dayPopoverAnchor);
              const totalCount = dayTasks.length + dayLeaves.length + dayHolidays.length;

              type CellItem =
                | { kind: 'holiday'; id: string; title: string; color: string }
                | { kind: 'leave'; id: string; title: string; color: string }
                | { kind: 'task'; task: Task };
              const cellItems: CellItem[] = [];
              for (const holiday of dayHolidays) {
                if (cellItems.length >= MONTH_CELL_MAX_ITEMS) break;
                cellItems.push({
                  kind: 'holiday',
                  id: holiday.id,
                  title: holiday.title,
                  color: holiday.color || '#dc2626',
                });
              }
              for (const leave of dayLeaves) {
                if (cellItems.length >= MONTH_CELL_MAX_ITEMS) break;
                const statusColors = {
                  [LeaveStatus.PENDING]: '#f59e0b',
                  [LeaveStatus.APPROVED]: '#10b981',
                  [LeaveStatus.REJECTED]: '#ef4444',
                  [LeaveStatus.CANCELLED]: '#94a3b8',
                  [LeaveStatus.WITHDRAWN]: '#94a3b8',
                };
                cellItems.push({
                  kind: 'leave',
                  id: leave.id,
                  title: leave.userName || leave.userId,
                  color: statusColors[leave.status] || '#94a3b8',
                });
              }
              for (const task of dayTasks) {
                if (cellItems.length >= MONTH_CELL_MAX_ITEMS) break;
                cellItems.push({ kind: 'task', task });
              }
              const hiddenCount = Math.max(0, totalCount - cellItems.length);

              return (
                <Box
                  key={day.format('YYYY-MM-DD')}
                  onClick={(e) => {
                    if (isMobile) {
                      handleMobileDateTap(day);
                    } else {
                      openDayAgenda(day, e.currentTarget);
                    }
                  }}
                  sx={{
                    ...cellSx,
                    px: { xs: 0.25, md: 0.5 },
                    pt: { xs: 0.25, md: 0.5 },
                    pb: 0.25,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: isSelected
                      ? 'primary.50'
                      : dayHolidays.length > 0
                        ? alpha(dayHolidays[0].color || '#dc2626', 0.06)
                        : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:active': isMobile ? { bgcolor: 'action.selected' } : {},
                  }}
                >
                  {/* Date number — Google-style circle for today */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: isMobile ? 'center' : 'flex-start',
                      alignItems: 'center',
                      height: { xs: 24, md: MONTH_DATE_ROW_H },
                      flexShrink: 0,
                      pl: { md: 0.25 },
                    }}
                  >
                    <Typography
                      variant={isMobile ? 'caption' : 'body2'}
                      fontWeight={isToday ? 700 : 500}
                      sx={{
                        width: { xs: 22, md: 26 },
                        height: { xs: 22, md: 26 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        bgcolor: isToday ? 'primary.main' : 'transparent',
                        color: isToday ? 'white' : 'text.primary',
                        fontSize: { xs: '0.75rem', md: '0.75rem' },
                      }}
                    >
                      {day.date()}
                    </Typography>
                  </Box>

                  {isMobile && totalCount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25, mt: 0.25 }}>
                      {dayHolidays.slice(0, 1).map((holiday) => (
                        <Box
                          key={holiday.id}
                          sx={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            bgcolor: holiday.color || '#dc2626',
                          }}
                        />
                      ))}
                      {dayLeaves.slice(0, 1).map((leave) => (
                        <Box
                          key={leave.id}
                          sx={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            bgcolor: leave.status === LeaveStatus.APPROVED ? '#10b981' : '#f59e0b',
                          }}
                        />
                      ))}
                      {dayTasks.slice(0, 3).map((task) => (
                        <Box
                          key={task.id}
                          sx={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            bgcolor: getPriorityColor(task.priority),
                          }}
                        />
                      ))}
                      {totalCount > 5 && (
                        <Typography variant="caption" sx={{ fontSize: '0.5rem', lineHeight: 1 }}>
                          +
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Desktop: Google-style dense chips + "+N more" */}
                  {!isMobile && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                      }}
                    >
                      {cellItems.map((item) => {
                        if (item.kind === 'holiday' || item.kind === 'leave') {
                          return (
                            <Tooltip key={`${item.kind}-${item.id}`} title={item.title}>
                              <Box
                                sx={{
                                  height: MONTH_CHIP_H,
                                  px: 0.75,
                                  borderRadius: 0.75,
                                  bgcolor: item.color,
                                  minWidth: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontSize: '0.6875rem',
                                    lineHeight: 1,
                                    color: '#fff',
                                  }}
                                >
                                  {item.title}
                                </Typography>
                              </Box>
                            </Tooltip>
                          );
                        }

                        const task = item.task;
                        const isBlocked = (task.blockedByTaskIds?.length || 0) > 0;
                        const chipColor = isBlocked
                          ? '#f59e0b'
                          : task.isRecurring
                            ? '#8b5cf6'
                            : getPriorityColor(task.priority);
                        return (
                          <Tooltip key={task.id} title={task.title}>
                            <Box
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTaskClick(task.id);
                              }}
                              sx={{
                                height: MONTH_CHIP_H,
                                px: 0.75,
                                borderRadius: 0.75,
                                bgcolor: chipColor,
                                minWidth: 0,
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                opacity: task.status === TaskStatus.COMPLETED ? 0.65 : 1,
                                '&:hover': { filter: 'brightness(0.92)' },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 500,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.6875rem',
                                  lineHeight: 1,
                                  color: '#fff',
                                  textDecoration:
                                    task.status === TaskStatus.COMPLETED ? 'line-through' : 'none',
                                }}
                              >
                                {task.title}
                              </Typography>
                            </Box>
                          </Tooltip>
                        );
                      })}
                      {hiddenCount > 0 && (
                        <Typography
                          component="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDayAgenda(day, e.currentTarget);
                          }}
                          sx={{
                            border: 0,
                            background: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontSize: '0.6875rem',
                            fontWeight: 600,
                            color: 'text.secondary',
                            px: 0.5,
                            py: 0,
                            height: MONTH_CHIP_H,
                            lineHeight: `${MONTH_CHIP_H}px`,
                            '&:hover': { color: 'primary.main', bgcolor: 'action.hover', borderRadius: 0.5 },
                          }}
                        >
                          +{hiddenCount} more
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        ) : (
          // Week View — fixed-height scrollable columns
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              minHeight: { xs: 360, md: 480 },
              maxHeight: { xs: '60vh', md: '65vh' },
            }}
          >
            {getWeekDays().map((day, index) => {
              const dayTasks = getTasksForDate(day);
              const dayLeaves = getLeavesForDate(day);
              const dayHolidays = getHolidaysForDate(day);
              const isToday = day.isSame(dayjs(), 'day');
              const visibleTasks = dayTasks.slice(0, WEEK_VISIBLE_TASKS);
              const hiddenTasks = dayTasks.length - visibleTasks.length;

              return (
                <Box
                  key={day.format('YYYY-MM-DD')}
                  onClick={(e) => openDayAgenda(day, e.currentTarget)}
                  sx={{
                    minHeight: 0,
                    maxHeight: '100%',
                    p: 1,
                    cursor: 'pointer',
                    borderRight: index < 6 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    bgcolor: isToday ? 'primary.50' : 'transparent',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  {/* Date */}
                  <Box sx={{ textAlign: 'center', mb: 1, flexShrink: 0 }}>
                    <Typography
                      variant="h5"
                      fontWeight={isToday ? 700 : 500}
                      sx={{ color: isToday ? 'primary.main' : 'text.primary', lineHeight: 1.2 }}
                    >
                      {day.date()}
                    </Typography>
                    {(dayTasks.length > 0 || dayLeaves.length > 0 || dayHolidays.length > 0) && (
                      <Typography variant="caption" color="text.secondary">
                        {dayTasks.length + dayLeaves.length + dayHolidays.length} items
                      </Typography>
                    )}
                  </Box>

                  {/* Scrollable events */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      flex: 1,
                      minHeight: 0,
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      pr: 0.25,
                    }}
                  >
                    {dayHolidays.map((holiday) => (
                      <Box
                        key={holiday.id}
                        sx={{
                          p: 0.75,
                          borderRadius: 1,
                          bgcolor: `${holiday.color || '#dc2626'}14`,
                          borderLeft: '3px solid',
                          borderColor: holiday.color || '#dc2626',
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          noWrap
                          sx={{ color: holiday.color || '#dc2626', display: 'block' }}
                        >
                          {holiday.title}
                        </Typography>
                      </Box>
                    ))}
                    {dayLeaves.map((leave) => (
                      <Box
                        key={leave.id}
                        sx={{
                          p: 0.75,
                          borderRadius: 1,
                          bgcolor: leave.status === LeaveStatus.APPROVED ? '#10b98114' : '#f59e0b14',
                          borderLeft: '3px solid',
                          borderColor: leave.status === LeaveStatus.APPROVED ? '#10b981' : '#f59e0b',
                          flexShrink: 0,
                        }}
                      >
                        <Typography variant="caption" fontWeight={600} noWrap display="block">
                          {leave.userName || leave.userId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {String(leave.type).replace('_', ' ')}
                        </Typography>
                      </Box>
                    ))}
                    {visibleTasks.map((task) => {
                      const isBlocked = (task.blockedByTaskIds?.length || 0) > 0;
                      const IssueIcon = task.issueType ? issueTypeConfig[task.issueType]?.icon : null;

                      return (
                        <Box
                          key={task.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTaskClick(task.id);
                          }}
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            borderLeft: '3px solid',
                            borderColor: isBlocked
                              ? '#f59e0b'
                              : task.isRecurring
                                ? '#8b5cf6'
                                : getPriorityColor(task.priority),
                            bgcolor: isBlocked
                              ? alpha('#f59e0b', 0.1)
                              : task.isRecurring
                                ? alpha('#8b5cf6', 0.08)
                                : 'background.paper',
                            border: '1px solid',
                            borderTopColor: 'divider',
                            borderRightColor: 'divider',
                            borderBottomColor: 'divider',
                            cursor: 'pointer',
                            flexShrink: 0,
                            minWidth: 0,
                            '&:hover': { boxShadow: 1 },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                            {task.isRecurring && <Repeat size={12} style={{ color: '#8b5cf6', flexShrink: 0 }} />}
                            {IssueIcon && (
                              <IssueIcon
                                size={12}
                                style={{ color: issueTypeConfig[task.issueType!].color, flexShrink: 0 }}
                              />
                            )}
                            {isBlocked && <Lock size={12} style={{ color: '#f59e0b', flexShrink: 0 }} />}
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              noWrap
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                textDecoration:
                                  task.status === TaskStatus.COMPLETED ? 'line-through' : 'none',
                              }}
                            >
                              {task.title}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <PriorityBadge priority={task.priority} size="small" />
                            {task.category && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {task.category}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                    {hiddenTasks > 0 && (
                      <Typography
                        component="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDayAgenda(day, e.currentTarget);
                        }}
                        variant="caption"
                        color="primary.main"
                        fontWeight={600}
                        sx={{
                          textAlign: 'center',
                          py: 0.5,
                          border: 0,
                          background: 'none',
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        +{hiddenTasks} more
                      </Typography>
                    )}
                    {dayTasks.length === 0 && dayLeaves.length === 0 && dayHolidays.length === 0 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textAlign: 'center', py: 2 }}
                      >
                        No tasks
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Card>

      {/* Google Calendar–style day agenda popover */}
      <Popover
        open={Boolean(dayPopoverAnchor) && Boolean(selectedDate)}
        anchorEl={dayPopoverAnchor}
        onClose={handleDayPopoverClose}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: 320,
              maxWidth: '90vw',
              maxHeight: 420,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              overflow: 'hidden',
            },
          },
        }}
      >
        {selectedDate && (
          <>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box>
                <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  {selectedDate.format('dddd')}
                </Typography>
                <Typography variant="h5" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                  {selectedDate.format('D')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedDate.format('MMMM YYYY')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button size="small" startIcon={<Plus size={14} />} onClick={handleCreateTask}>
                  Add
                </Button>
                <IconButton size="small" onClick={handleDayPopoverClose} aria-label="Close">
                  <X size={16} />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1 }}>
              {(() => {
                const agendaHolidays = getHolidaysForDate(selectedDate);
                const agendaLeaves = getLeavesForDate(selectedDate);
                const agendaTasks = getTasksForDate(selectedDate);
                const empty =
                  agendaHolidays.length === 0
                  && agendaLeaves.length === 0
                  && agendaTasks.length === 0;

                if (empty) {
                  return (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Calendar size={36} style={{ color: '#94a3b8', marginBottom: 8 }} />
                      <Typography variant="body2" color="text.secondary">
                        No events for this day
                      </Typography>
                    </Box>
                  );
                }

                return (
                  <Stack spacing={0.75}>
                    {agendaHolidays.map((holiday) => (
                      <Box
                        key={holiday.id}
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          bgcolor: holiday.color || '#dc2626',
                          color: '#fff',
                        }}
                      >
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {holiday.title}
                        </Typography>
                      </Box>
                    ))}
                    {agendaLeaves.map((leave) => {
                      const color =
                        leave.status === LeaveStatus.APPROVED ? '#10b981' : '#f59e0b';
                      return (
                        <Box
                          key={leave.id}
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            bgcolor: color,
                            color: '#fff',
                          }}
                        >
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {leave.userName || leave.userId}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>
                            {String(leave.type).replace('_', ' ')} · {leave.status}
                          </Typography>
                        </Box>
                      );
                    })}
                    {agendaTasks.map((task) => {
                      const isBlocked = (task.blockedByTaskIds?.length || 0) > 0;
                      const color = isBlocked
                        ? '#f59e0b'
                        : task.isRecurring
                          ? '#8b5cf6'
                          : getPriorityColor(task.priority);
                      return (
                        <Box
                          key={task.id}
                          onClick={() => {
                            handleDayPopoverClose();
                            handleTaskClick(task.id);
                          }}
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            bgcolor: color,
                            color: '#fff',
                            cursor: 'pointer',
                            opacity: task.status === TaskStatus.COMPLETED ? 0.7 : 1,
                            '&:hover': { filter: 'brightness(0.92)' },
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            noWrap
                            sx={{
                              textDecoration:
                                task.status === TaskStatus.COMPLETED ? 'line-through' : 'none',
                            }}
                          >
                            {task.title}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.25, alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>
                              {task.priority.toUpperCase()}
                            </Typography>
                            {task.category && (
                              <Typography variant="caption" sx={{ opacity: 0.9 }} noWrap>
                                {task.category}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                );
              })()}
            </Box>

            <Divider />
            <Box sx={{ px: 1.5, py: 1 }}>
              <Button fullWidth size="small" variant="text" onClick={handleViewAllTasks}>
                View all tasks
              </Button>
            </Box>
          </>
        )}
      </Popover>

      {/* Mobile Task Drawer */}
      <SwipeableDrawer
        anchor="bottom"
        open={mobileTaskDrawer.open}
        onClose={() => setMobileTaskDrawer({ open: false, date: null, tasks: [] })}
        onOpen={() => {}}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '70vh',
            pb: 'env(safe-area-inset-bottom)',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Handle */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box sx={{ width: 40, height: 4, bgcolor: 'grey.300', borderRadius: 2 }} />
          </Box>
          
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              {mobileTaskDrawer.date?.format('dddd, MMM D')}
            </Typography>
            <Button size="small" startIcon={<Plus size={14} />} onClick={handleCreateTask}>
              Add
            </Button>
          </Box>

          {mobileTaskDrawer.date && getHolidaysForDate(mobileTaskDrawer.date).map((holiday) => (
            <Box
              key={holiday.id}
              sx={{
                mb: 1.5,
                p: 1.25,
                borderRadius: 2,
                bgcolor: `${holiday.color || '#dc2626'}14`,
                borderLeft: '4px solid',
                borderColor: holiday.color || '#dc2626',
              }}
            >
              <Typography variant="body2" fontWeight={600} sx={{ color: holiday.color || '#dc2626' }}>
                {holiday.title}
              </Typography>
            </Box>
          ))}
          {mobileTaskDrawer.date && getLeavesForDate(mobileTaskDrawer.date).map((leave) => (
            <Box
              key={leave.id}
              sx={{
                mb: 1.5,
                p: 1.25,
                borderRadius: 2,
                bgcolor: leave.status === LeaveStatus.APPROVED ? '#10b98114' : '#f59e0b14',
                borderLeft: '4px solid',
                borderColor: leave.status === LeaveStatus.APPROVED ? '#10b981' : '#f59e0b',
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {leave.userName || leave.userId}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {String(leave.type).replace('_', ' ')} · {leave.status}
              </Typography>
            </Box>
          ))}
          {/* Tasks */}
          {mobileTaskDrawer.tasks.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Calendar size={40} style={{ color: '#94a3b8', marginBottom: 8 }} />
              <Typography variant="body2" color="text.secondary">
                No tasks for this day
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {mobileTaskDrawer.tasks.map((task) => (
                <Card
                  key={task.id}
                  onClick={() => {
                    setMobileTaskDrawer({ open: false, date: null, tasks: [] });
                    handleTaskClick(task.id);
                  }}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    borderLeft: '3px solid',
                    borderLeftColor: task.isRecurring ? '#8b5cf6' : getPriorityColor(task.priority),
                    bgcolor: task.isRecurring ? alpha('#8b5cf6', 0.05) : 'background.paper',
                    cursor: 'pointer',
                    '&:active': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                        {task.isRecurring && (
                          <Repeat size={14} style={{ color: '#8b5cf6' }} />
                        )}
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {task.title}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <PriorityBadge priority={task.priority} size="small" />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <Clock size={12} style={{ color: '#94a3b8' }} />
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(task.dueDate).format('MMM D')}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <ChevronRightIcon size={18} style={{ color: '#94a3b8' }} />
                  </Box>
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      </SwipeableDrawer>

      {/* Mobile FAB */}
      {isMobile && (
        <Fab
          color="primary"
          onClick={handleCreateTask}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            zIndex: 1100,
          }}
        >
          <Plus size={24} />
        </Fab>
      )}
    </Box>
  );
};

export default CalendarPage;
